import { useCallback, useEffect, useMemo, useState } from "react";

import { defaults } from "chart.js";
import { format } from "date-fns";
import { useRouteMatch } from "react-router-dom";

import { getPublicReportFromToken } from "@/api/endpoints/public";
import LogoDark from "@/assets/images/allears-dark.svg";
import MentionsModalURL from "@/containers/MentionsModalURL";
import {
  MENTIONS_MODAL_OPEN_PARAM,
  MODAL_FILTER_PREFIX,
} from "@/containers/MentionsModalURL/settings";
import PublicLayout from "@/containers/PublicLayout";
import useSearchParams from "@/hooks/useSearchParams";
import { ROUTES } from "@/routes_new";
import { cx, prefixKeys, removeSuffix } from "@/utils";
import { autoLayout } from "@/views/reports/layout";
import Spinner from "@components/Spinner";
import Typography from "@components/Typography";

import TopWidgets from "../../MentionsView/partials/TopWidgets";
import { mirrorWidgetsForTimeComparisonReport } from "../helpers.js";
import { MentionsModal } from "../MentionsModal";
import { PublicReportMenu } from "../PublicReportMenu";
import { Widget } from "../widgets/Widget";

import styles from "./ReportsPublicDetailView.module.css";

// Disable animations when printing PDF
if (window.navigator.userAgent.indexOf("HeadlessChrome") > 0) {
  defaults.animation = false;
}

// for time comparison reports
const dateFormats = {
  yearly: "yyyy",
  quarterly: "QQQ, yyyy",
  monthly: "MMM, yyyy",
  weekly: "yyyy-MM-dd",
  daily: "yyyy-MM-dd",
};

export const ReportsPublicDetailView = () => {
  const [report, setReport] = useState({});
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [compareStartDate, setCompareStartDate] = useState("");
  const [compareEndDate, setCompareEndDate] = useState("");
  const [isSocial, setIsSocial] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [alertUuid, setAlertUuid] = useState(null);
  const [filters, setFilters] = useState();

  useEffect(() => {
    // Remove this class to signal that frontend js is loaded.
    document.getElementById("root-app")?.classList.remove("js-loading");
  }, []);

  // MentionsModal
  const [showMentionsModal, setShowMentionsModal] = useState(false);
  const [fetchMentionsParams, setFetchMentionsParams] = useState({});
  const isInsight = useRouteMatch(ROUTES.INSIGHTS_PUBLIC);

  const [params, setParams] = useSearchParams();

  const fetchMentions = useCallback(
    (sources, filters = {}) => {
      if (!filters.dates) filters.dates = [startDate, endDate];

      setFetchMentionsParams({
        report: report.uuid,
        sources: sources,
        filters: filters,
        timestamp: Date.now(), // to trigger a new fetch
      });

      setShowMentionsModal(true);
    },
    [report.uuid, endDate, startDate]
  );

  useEffect(() => {
    document.title = report ? `${report.title} — All Ears` : `All Ears`;
  }, [report]);

  useEffect(() => {
    // This could be swapped for useParams, IF, we start rendering this page as an actual route. right now it is just rendered into a portal.
    const tokenRegex = /(reports|insights)\/t\/([a-zA-Z0-9]+)/;
    const tokenMatches = tokenRegex.exec(window.location.href);
    const token = tokenMatches[2];

    function getFilters() {
      const decodedToken = atob(atob(token).split(":")[0]);
      const parsedToken = JSON.parse(decodedToken);

      setFilters({
        start_date: parsedToken.start_date,
        end_date: parsedToken.end_date,
        sort_by: parsedToken.sort_by,
        channel_type: parsedToken.channel_type,
        channel_name: parsedToken.channel_name,
        freetext: parsedToken.freetext,
        sentiment: parsedToken.sentiment,
        category: parsedToken.category,
        language: parsedToken.language,
        mention_kind: parsedToken.mention_kind,
        min_reach_threshold: parsedToken.min_reach_threshold,
        insight_id: parsedToken.insight_id,
        insight_types: parsedToken.insight_types,
      });
    }

    getFilters();

    getPublicReportFromToken(token).then(({ data }) => {
      setStartDate(data.start_date);
      setEndDate(data.end_date);
      setCompareStartDate(data.compare_start_date);
      setCompareEndDate(data.compare_end_date);
      setIsSocial(data.report?.show_socialmedia_widget);
      setReport(data.report);
      setPdfUrl(data.pdf_url);
    });
  }, []);

  const [prevReport, setPrevReport] = useState(report);
  if (report !== prevReport) {
    setPrevReport(report);
    const firstSearchUuid = report?.widgets?.[0]?.searches?.[0]?.uuid;
    if (report?.template === "insights" && firstSearchUuid) {
      setAlertUuid(firstSearchUuid);
    } else {
      setAlertUuid(null);
    }
  }

  const widgetsByRow = useMemo(() => {
    if (report?.template === "insights" && !alertUuid) {
      return [];
    }

    let widgets = report?.widgets;

    // mirror time comparison template
    if (report?.template === "time_comparison") {
      widgets = mirrorWidgetsForTimeComparisonReport(
        widgets,
        compareStartDate,
        compareEndDate,
        true
      );
    }

    // layout widgets (if any?)
    widgets = widgets
      ? autoLayout(
          // only set insights-social if insights report
          isSocial && report.template === "insights"
            ? "insights-social"
            : report.template,
          widgets
        ).map((widget, i) => {
          const size = params.ppt ? 12 : widget.size;

          return (
            <Widget
              {...widget}
              filters={filters}
              searchId={alertUuid}
              size={size}
              reportUuid={report.uuid}
              reportTemplate={report.template}
              interval={report.interval}
              key={`${widget?.uuid || i}-${widget.mirrored ? "mirrored" : ""}`}
              startDate={widget.startDate || startDate}
              endDate={widget.endDate || endDate}
              fetchMentions={fetchMentions}
              fetchMentions_v2={(widgetSpecificFilters) => {
                // Prefix all keys with the modal specific settings
                const filters = removeSuffix(widgetSpecificFilters, "_filter");
                const f = prefixKeys(MODAL_FILTER_PREFIX, {
                  searches: widget.searches.map((search) => search.uuid),
                  report: report?.uuid,
                  end_date: endDate,
                  start_date: startDate,
                  ...filters,
                });

                // Update the query params to trigger a new fetch
                setParams({ ...f, [MENTIONS_MODAL_OPEN_PARAM]: widget.metric });
              }}
            />
          );
        })
      : [];

    // split allWidgets array to rows so they can use page breaks
    let gridSize = 12;

    let rows = widgets.reduce(
      (acc, widget) => {
        let colCounter = acc.colCounter;
        const rowsBefore = Math.ceil(colCounter / gridSize),
          rowsAfter = Math.ceil(
            (colCounter + (params.ppt ? 12 : widget.props.size)) / gridSize
          );

        // start new row
        if (rowsBefore != rowsAfter) {
          acc.rows.push([]);
        }

        colCounter += widget.props.size;
        acc.rows[acc.rows.length - 1].push(widget);
        acc.colCounter = colCounter;

        return acc;
      },
      { rows: [[]], colCounter: 0 }
    ).rows;
    return rows;
  }, [
    setParams,
    report,
    startDate,
    filters,
    endDate,
    compareStartDate,
    compareEndDate,
    isSocial,
    fetchMentions,
    alertUuid,
    params.ppt,
  ]);

  return (
    <PublicLayout
      variant={isInsight ? "white" : "gray"}
      navClassName={styles.nav}
      clasName={styles.layout}
      menu={<PublicReportMenu pdfUrl={pdfUrl} className={styles.nav} />}
    >
      {report ? (
        <div
          className={`template-${report.template} mx-auto max-w-(--site-content-max-width)`}
        >
          <LogoDark width="90" className="hidden print:inline-block" alt="" />

          <div className="flex-1 justify-center items-stretch">
            <header className="text-center w-full mt-4">
              <Typography
                align="center"
                block
                variant="h1"
                margin="none"
                className={styles.title}
                data-testid="public-report-title"
              >
                {report.title}
              </Typography>

              {report.template !== "time_comparison" && (
                <Typography align="center" block variant="h2">
                  {startDate && endDate
                    ? `${startDate} – ${endDate}`
                    : endDate
                      ? endDate
                      : ""}
                </Typography>
              )}
            </header>
          </div>
          {alertUuid && isInsight && filters && (
            <div>
              <TopWidgets
                isPublicInsightsPage={true}
                className={styles.top}
                isInsightsPage={true}
                alertUuid={alertUuid}
                filters={filters}
              />
            </div>
          )}

          {report.template === "time_comparison" && (
            <div className="mt-3">
              <div>
                <Typography variant="h3" align="center" block>
                  {format(
                    new Date(compareStartDate),
                    dateFormats[report.interval]
                  )}
                </Typography>
              </div>

              <div>
                <Typography variant="h3" align="center" block>
                  {format(new Date(startDate), dateFormats[report.interval])}
                </Typography>
              </div>
            </div>
          )}

          <div>
            <div
              className={cx({
                [styles.container]: report.template === "time_comparison",
              })}
            >
              {widgetsByRow.map((row, i) => (
                <div key={i} className={cx("grid grid-cols-12", styles.break)}>
                  {row}
                </div>
              ))}
            </div>
          </div>

          {/* Modal to show mentions when clicking in the charts */}
          <MentionsModal
            show={showMentionsModal}
            onHide={() => setShowMentionsModal(false)}
            params={fetchMentionsParams}
          />
          <MentionsModalURL filters={fetchMentionsParams.filters} />
        </div>
      ) : (
        <div className="flex justify-center">
          <Spinner className="report-loading" />
        </div>
      )}
    </PublicLayout>
  );
};
